import React from "react";
import { Routes as RouterRoutes, Route, Navigate, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import QRCodeGenerator from './pages/qr-code-generator';
import RestaurantMenuLanding from './pages/restaurant-menu-landing';
import ARViewer from './pages/ar-viewer';
import RestaurantAdminDashboard from './pages/restaurant-admin-dashboard';
import ManagementContentEditor from './pages/management-content-editor';
import ModalitesPolitiques from './pages/modalites-politiques';
import SuperAdminDashboard from './pages/super-admin-dashboard';
import FranchiseeDashboard from './pages/franchisee-dashboard';
import { useAuth } from './contexts/AuthContext';
import { useTenant } from './contexts/TenantContext';
import AccessDenied from './pages/AccessDenied';
import RestaurantNotFound from './pages/RestaurantNotFound';
import AcceptInvite from './pages/AcceptInvite';
import ResetPassword from './pages/reset-password';
import { useEffect } from 'react';

// Intercepts Supabase recovery tokens (hash or query) that land on any page
// and redirects to /reset-password before anything else renders.
const RecoveryRedirect = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const hash = window.location?.hash || '';
  const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(location?.search || window.location?.search || '');

  const hashType = hashParams?.get('type');
  const accessToken = hashParams?.get('access_token');
  const queryType = searchParams?.get('type');
  const tokenHash = searchParams?.get('token_hash');
  const hashError = hashParams?.get('error_code') || hashParams?.get('error');

  // Implicit flow: #access_token=...&type=recovery
  // Email OTP / template links: ?token_hash=...&type=recovery
  // Failed verify: #error=access_denied&error_code=otp_expired
  const isRecoveryToken =
    (hashType === 'recovery' && !!accessToken) ||
    (queryType === 'recovery' && !!tokenHash) ||
    !!hashError;
  const isAlreadyOnResetPage = location?.pathname === '/reset-password';

  useEffect(() => {
    if (isRecoveryToken && !isAlreadyOnResetPage) {
      const search = window.location?.search || '';
      const nextHash = window.location?.hash || '';
      navigate(`/reset-password${search}${nextHash}`, { replace: true });
    }
  }, [isRecoveryToken, isAlreadyOnResetPage, navigate]);

  // Block rendering until redirect completes — prevents menu page from flashing
  if (isRecoveryToken && !isAlreadyOnResetPage) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return children;
};

const ProtectedRoute = ({ children }) => {
  const {
    isAuthenticated,
    hasRestaurantAccess,
    canAccessRestaurant,
    isSuperAdmin,
    isFranchisee,
    loading,
    profileLoading,
    restaurantId: authRestaurantId,
    linkedRestaurantId,
  } = useAuth();
  const { isAdminHost, isLoading: tenantLoading, restaurantId: tenantRestaurantId } = useTenant();
  const [searchParams] = useSearchParams();
  const restaurantIdParam = searchParams?.get('restaurant_id');
  const requestedRestaurantId = restaurantIdParam || tenantRestaurantId || linkedRestaurantId || authRestaurantId || null;

  if (loading || tenantLoading || (isAuthenticated && profileLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/restaurant-admin-dashboard" replace />;
  // Allow super admins to manage a specific restaurant; only bounce them home when no restaurant is selected
  if (isAdminHost && isSuperAdmin && !restaurantIdParam) {
    return <Navigate to="/super-admin" replace />;
  }
  // Franchisees without a specific restaurant go to their dashboard
  if (isAdminHost && isFranchisee && !restaurantIdParam) {
    return <Navigate to="/franchisee" replace />;
  }

  // Restaurant admins may open QR/editor via their subdomain even when ?restaurant_id= is missing.
  // Deny only when they have no restaurant relationship and no tenant/profile restaurant context.
  // Unlinked admins on their restaurant domain still resolve a tenant id (dashboard works) —
  // allow QR/editor in that case instead of a false Access Denied.
  const allowed =
    isSuperAdmin
    || isFranchisee
    || hasRestaurantAccess
    || (requestedRestaurantId && canAccessRestaurant?.(requestedRestaurantId))
    || (!!tenantRestaurantId && isAuthenticated && !isAdminHost && !linkedRestaurantId);
  if (!allowed) return <Navigate to="/access-denied" replace />;

  // Prefer the admin's linked restaurant id for URL context; fall back to auth/tenant id.
  const injectRestaurantId = linkedRestaurantId || authRestaurantId || tenantRestaurantId;
  if (!isSuperAdmin && !isFranchisee && !restaurantIdParam && injectRestaurantId) {
    const currentPath = window.location?.pathname;
    const currentSearch = window.location?.search;
    const sep = currentSearch ? '&' : '?';
    return <Navigate to={`${currentPath}${currentSearch}${sep}restaurant_id=${injectRestaurantId}`} replace />;
  }

  return children;
};

const TenantAwareRoot = () => {
  const { isNotFound, slug } = useTenant();
  if (isNotFound && slug) return <RestaurantNotFound />;
  return <RestaurantMenuLanding />;
};

const Routes = () => {
  return (
      <ErrorBoundary>
      <ScrollToTop />
      <RecoveryRedirect>
      <RouterRoutes>
        {/* Define your route here */}
        <Route path="/" element={<TenantAwareRoot />} />
        <Route
          path="/qr-code-generator"
          element={
            <ProtectedRoute>
              <QRCodeGenerator />
            </ProtectedRoute>
          }
        />
        <Route path="/restaurant-menu-landing" element={<TenantAwareRoot />} />
        <Route path="/ar-viewer" element={<ARViewer />} />
        <Route path="/restaurant-admin-dashboard" element={<RestaurantAdminDashboard />} />
        <Route path="/dashboard" element={<RestaurantAdminDashboard />} />
        <Route
          path="/management-content-editor"
          element={
            <ProtectedRoute>
              <ManagementContentEditor />
            </ProtectedRoute>
          }
        />
        <Route path="/modalites-politiques" element={<ModalitesPolitiques />} />
        <Route path="/franchisee" element={<FranchiseeDashboard />} />
        <Route path="/super-admin" element={<SuperAdminDashboard />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="/restaurant-not-found" element={<RestaurantNotFound />} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </RecoveryRedirect>
      </ErrorBoundary>
  );
};
export default Routes;
